
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WelcomeScreen from './steps/WelcomeScreen';
import InterestSelection from './steps/InterestSelection';
import PersonalizedFeed from './steps/PersonalizedFeed';
import PaymentSetup from './steps/PaymentSetup';
import SubscriptionOptions from './steps/SubscriptionOptions';
import FeatureTour from './steps/FeatureTour';
import FirstArticle from './steps/FirstArticle';
import EmailPreferences from './steps/EmailPreferences';
import FriendFinder from './steps/FriendFinder';
import { useToast } from '@/hooks/use-toast';

const ReaderOnboarding: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    interests: [] as string[],
    journalists: [] as number[],
    paymentMethod: null,
    subscription: '',
    emailFrequency: 'daily',
    connectedFriends: [] as string[],
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const totalSteps = 9;
  
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Complete onboarding
      toast({
        title: "Onboarding complete!",
        description: "Your reader profile has been set up successfully.",
      });
      navigate('/home');
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  
  const updateFormData = (data: Partial<typeof formData>) => {
    setFormData({
      ...formData,
      ...data,
    });
  };
  
  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeScreen onNext={handleNext} />;
      case 2:
        return (
          <InterestSelection
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <PersonalizedFeed
            formData={formData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <PaymentSetup
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <SubscriptionOptions
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 6:
        return (
          <FeatureTour
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 7:
        return (
          <FirstArticle
            formData={formData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 8:
        return (
          <EmailPreferences
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 9:
        return (
          <FriendFinder
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      default:
        return null;
    }
  };
  
  return renderStep();
};

export default ReaderOnboarding;
